'use client';

import { useState } from 'react';
import { useBrowserState } from '@/src/lib/use-browser-state';
import {
  acknowledgeEvent,
  createEvent,
  deleteEvent,
  getDaysInMonth,
  getEventsForDate,
  getFirstDayOfMonth,
  getRelativeDay,
  getUpcomingEvents,
  loadEvents,
  typeColorVar,
  typeEmoji,
  typeLabel,
  type CalendarEvent,
  type EventType,
} from '@/src/lib/calendar-events';

const EMPTY_EVENTS: CalendarEvent[] = [];

export function CalendarMiniView() {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useBrowserState(loadEvents, EMPTY_EVENTS);
  const [currentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftType, setDraftType] = useState<EventType>('meeting');
  const [draftDate, setDraftDate] = useState(new Date().toISOString().slice(0, 10));

  function refresh() {
    setEvents(loadEvents());
  }

  function handleCreateEvent() {
    if (!draftTitle.trim()) return;
    createEvent({ title: draftTitle, type: draftType, date: new Date(draftDate).toISOString() });
    setDraftTitle('');
    refresh();
  }

  function handleAcknowledge(id: string) {
    acknowledgeEvent(id);
    refresh();
  }

  function handleDelete(id: string) {
    deleteEvent(id);
    refresh();
  }

  const upcoming = getUpcomingEvents(30, events);
  const upcomingCount = upcoming.filter((e) => !e.acknowledged).length;

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = (getFirstDayOfMonth(year, month) + 6) % 7;
  const cells: Array<{ day: number | null; date?: Date }> = [];
  for (let i = 0; i < firstDay; i++) cells.push({ day: null });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, date: new Date(year, month, d) });

  return (
    <div className="relative" data-tour="calendar-mini-view">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Kalender öffnen"
        data-tour="calendar-toggle"
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] text-[var(--k-color-text-secondary)] shadow-[var(--k-shadow-xs)] transition hover:border-[var(--k-color-border-default)] hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]"
      >
        📅
        {upcomingCount > 0 ? (
          <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--k-color-warning-strong)] px-1.5 text-[10px] font-bold text-[var(--k-color-text-primary)] ring-2 ring-[var(--k-color-surface)]">
            {upcomingCount > 9 ? '9+' : upcomingCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          data-tour="calendar-popover"
          className="absolute right-0 top-11 z-30 w-96 rounded-md border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] shadow-xl"
        >
          <header className="flex items-center justify-between border-b border-[var(--k-color-border-subtle)] p-3">
            <div>
              <p className="text-sm font-semibold">Kalender</p>
              <p className="text-[10px] text-[var(--k-color-text-muted)]">{currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Schließen" className="rounded p-1 hover:bg-[var(--k-color-surface-muted)]">✕</button>
          </header>

          <div className="grid grid-cols-7 gap-0.5 p-2 text-center text-[10px]">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
              <div key={d} className="font-semibold text-[var(--k-color-text-muted)]">{d}</div>
            ))}
            {cells.map((c, i) => {
              if (c.day === null) return <div key={i} />;
              const dayEvents = c.date ? getEventsForDate(c.date, events) : [];
              const isToday = c.date?.toDateString() === new Date().toDateString();
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelectedDate(c.date ?? null)}
                  data-tour={c.date ? `cal-day-${c.date.toISOString().slice(0, 10)}` : undefined}
                  aria-label={c.date ? c.date.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }) : undefined}
                  className={`relative aspect-square rounded text-xs ${isToday ? 'bg-[var(--k-color-accent)] text-white' : 'hover:bg-[var(--k-color-surface-muted)]'} ${selectedDate?.toDateString() === c.date?.toDateString() ? 'ring-1 ring-[var(--k-color-accent)]' : ''}`}
                >
                  {c.day}
                  {dayEvents.length > 0 ? (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 inline-flex h-1 w-1 rounded-full bg-[var(--k-color-warning-strong)]" />
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="border-t border-[var(--k-color-border-subtle)] p-3">
            <p className="mb-2 text-xs font-semibold">Kommende Termine</p>
            {upcoming.length === 0 ? (
              <p className="text-[10px] text-[var(--k-color-text-muted)]">Keine bevorstehenden Termine.</p>
            ) : null}
            <ul className="max-h-40 space-y-1 overflow-y-auto">
              {upcoming.slice(0, 8).map((e) => (
                <li
                  key={e.id}
                  data-tour={`event-${e.id}`}
                  className={`flex items-center gap-2 rounded border px-2 py-1 text-[11px] ${e.acknowledged ? 'border-[var(--k-color-border-subtle)] opacity-60' : 'border-[var(--k-color-border-default)]'}`}
                >
                  <span>{typeEmoji(e.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold">{e.title}</p>
                    <p className="text-[9px]" style={{ color: typeColorVar(e.type) }}>
                      {typeLabel(e.type)} · {getRelativeDay(e.date)}
                    </p>
                  </div>
                  <button type="button" onClick={() => handleAcknowledge(e.id)} aria-label="Termin bestätigen" className="text-[10px] text-[var(--k-color-text-link)] hover:underline">✓</button>
                  <button type="button" onClick={() => handleDelete(e.id)} aria-label="Termin löschen" className="text-[10px] text-[var(--k-color-danger-strong)] hover:underline">✕</button>
                </li>
              ))}
            </ul>
          </div>

          <div className="border-t border-[var(--k-color-border-subtle)] p-3">
            <p className="mb-2 text-xs font-semibold">Neuer Termin</p>
            <input
              type="text"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="Titel"
              data-tour="event-title"
              className="mb-1 w-full rounded-md border border-[var(--k-color-border-default)] bg-[var(--k-color-surface-muted)] px-2 py-1 text-xs"
            />
            <div className="flex gap-1">
              <select value={draftType} onChange={(e) => setDraftType(e.target.value as EventType)} className="flex-1 rounded-md border border-[var(--k-color-border-default)] bg-[var(--k-color-surface-muted)] px-2 py-1 text-xs">
                {(['invoice-due', 'meeting', 'tax-deadline', 'reminder', 'workflow', 'milestone'] as const).map((t) => (
                  <option key={t} value={t}>{typeLabel(t)}</option>
                ))}
              </select>
              <input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} className="rounded-md border border-[var(--k-color-border-default)] bg-[var(--k-color-surface-muted)] px-2 py-1 text-xs" />
            </div>
            <button type="button" onClick={handleCreateEvent} data-tour="event-create" className="mt-2 w-full rounded-md bg-[var(--k-color-accent)] px-3 py-1 text-xs font-semibold text-white">
              Hinzufügen
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
